import Box from '@cashew/box';
import Button from '@cashew/button';
import ConfirmModal from '@cashew/confirm-modal';
import List from '@cashew/list';
import Tag from '@cashew/tag';
import type { order as orderType, product as productType } from '@cashew/types';
import { initialOrder } from '@cashew/types/lib/cjs/initialOrder';
import {
  api,
  dateFormat,
  orderMapStatus,
  paymentMethodFormatter,
  priceFormat,
  subscriptionTitleHelper,
} from '@cashew/utils';
import React, { useEffect, useState } from 'react';
import { BRAND_COLORS } from '../../config/brand';
import CancelModal from './CancelModal';
import ChargebackModal from './Chargeback';
import ChargebackList from './Chargeback/ChargebackList';
import DispatchModal from './DispatchModal';
import PaymentIconSvg from './PaymentIcon-svg';
import RefundModal from './RefundModal';

import './index.scss';

const ns = 'subscription-detail';

const useGetOrderDetails = (
  orderId: string,
  setOrderDetails: any,
  setLoading: any,
  dispatch: any,
  t: any
) => {
  setLoading(true);
  api
    .get({
      endpoint: `orders/${orderId}`,
    })
    .then(async (resp) => {
      const hasCustomer = false;
      // const hasCustomer = !!resp.data.customer;
      if (!hasCustomer) {
        await api
          .get({
            endpoint: `orders/${orderId}/customer`,
          })
          .then((customerResponse) => {
            // eslint-disable-next-line no-param-reassign
            resp.data.customer = customerResponse.data;
          })
          .catch((error) => {
            console.log(error);
          });
      }
      setOrderDetails({
        ...resp.data,
        merchantOrderCode: resp?.data?.sharafdgRef,
      });
    })
    .catch(() =>
      dispatch({
        type: 'OPEN_TOASTER',
        payload: {
          toasterTitle: t('orderDetails.error.fetchDetails.title'),
          toasterMessage: t('orderDetails.error.fetchDetails.message'),
          toasterType: 'error',
          toasterVisible: true,
          toasterDismiss: null,
        },
      })
    );
};

const getInstallmentDetails = (
  orderId: string,
  // @ts-ignore
  currency: any,
  setProduct: any,
  setNextPayment: any,
  setSubscriptionHistory: any,
  history: any
) => {
  api
    .get({
      endpoint: `products/options/${orderId}?paymentInfo=true`,
    })
    .then((resp) => {
      const { data } = resp;
      const pending = data.subProducts[0].installments.filter(
        (item: any) => item.status === 'PENDING'
      );
      setNextPayment(pending[0]);
      let subscriptionHistory = data.subProducts[0].installments.filter(
        (item: any) => item.status !== 'PENDING'
      );
      subscriptionHistory = subscriptionHistory.map((item: any) => ({
        ...item,
        currency: data.currencyCode,
        amount: item.installmentAmount,
      }));
      setSubscriptionHistory(subscriptionHistory.slice().reverse());
      setProduct(data);
    })
    .catch((resp) => {
      api.errorHandler({ resp, history });
    });
};

const SubscriptionDetailPage = ({
  language,
  t,
  useSelector,
  type,
  history,
  dispatch,
  Link,
  orderId,
  NotificationBadge,
  SubscriptionHistoryItem,
  portalName,
  moment,
}: any) => {
  const { token, role } = useSelector((state: any) => state.user);
  api.storeAuth(token);

  const [orderDetails, setOrderDetails] = useState<orderType.OrderType | any>(
    initialOrder
  );
  const [product, setProduct] = useState<productType.ProductType | undefined>(
    undefined
  );
  // @ts-ignore
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(false);
  const [orderStatus, setOrderStatus] = useState<any>(undefined);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [refundModal, setRefundModal] = useState(false);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [labelsArray, setLabelsArray] = useState<any[]>([]);
  const [eligibleDispatch, setEligibleDispatch] = useState(false);
  const [triggerApi, setTriggerApi] = useState(false);
  const [refundEligible, setRefundEligible] = useState<boolean>(false);
  const [isChargebackModalOpen, setIsChargebackModalOpen] = useState(false);
  const [ischargebackEligible, setIsChargebackEligible] = useState(false);
  const [chargebacks, setChargebacks] = useState([]);

  const [
    totalEligibleRefundSubscriptions,
    setTotalEligibleRefundSubscriptions,
  ] = useState(1);
  const [showCancelModalWarning, setShowCancelModalWarning] = useState(false);
  const [subscriptionPaymentChoice, setSubscriptionPaymentChoice] = useState({
    numbers: '',
    text: '',
  });
  const [nextPayment, setNextPayment] = useState({
    installmentAmount: 0,
    chargeDate: '',
    id: 0,
  });
  const roles = ['CASHEW', 'MERCHANT', 'MERCHANT_USER', 'MERCHANT_POS'];

  const checkChargebackEligibility = () => {
    api
      .get({
        endpoint: `payments/chargeback/${orderId}/eligibility`,
        headers: {
          Authorization: token,
        },
      })
      .then((resp) => {
        setIsChargebackEligible(resp.data.isPaymentChargebackEligible);
      })
      .catch(() => {
        setIsChargebackEligible(false);
      });
  };
  const getChargebacks = (params: { orderId: string }) => {
    if (orderId) {
      api
        .get({
          endpoint: `payments/chargeback/search?orderId=${params.orderId}`,
        })
        .then((resp) => {
          const list = resp?.data || [];
          setChargebacks(list);
        })
        .catch(() => {});
    }
  };

  useEffect(() => {
    if (orderDetails.productType === 'DIVIDE') {
      history.push(`/${language}/orders/${orderId}`);
    }

    if (type === 'admin') {
      const formattedStatus = orderMapStatus({
        type: 'subscription',
        status: orderDetails.orderStatus,
      });
      setOrderStatus(
        <Tag status={formattedStatus.className}>{formattedStatus.label}</Tag>
      );
    } else {
      setOrderStatus(
        orderDetails.orderStatus !== 'CANCELLED' ? (
          <Tag status="PAID">Active</Tag>
        ) : (
          <Tag status="CANCELLED">Cancelled</Tag>
        )
      );
    }

    checkChargebackEligibility();
    getChargebacks({ orderId });
  }, [orderDetails]);

  useEffect(() => {
    const { numbers, text } = paymentMethodFormatter(
      product?.subProducts[0].type || ''
    );
    setSubscriptionPaymentChoice({
      ...subscriptionPaymentChoice,
      numbers: `${numbers}`,
      text,
    });
  }, [product?.subProducts[0].type]);

  useEffect(() => {
    const labels = [
      {
        key: 'orderStatus',
        title: 'Subscription status',
        value: orderStatus,
        custom: true,
      },
      {
        key: 'chargeDate',
        title: 'Will renew on',
        value: nextPayment
          ? dateFormat({
              date: nextPayment?.chargeDate,
            })
          : '',
      },
      {
        key: 'customerName',
        title: 'Customer name',
        value: `${
          orderDetails.customer?.firstName
            ? orderDetails.customer?.firstName
            : '-- Not registered --'
        } ${
          orderDetails.customer?.lastName && orderDetails.customer?.firstName
            ? orderDetails.customer?.lastName
            : ''
        }`,
      },
      {
        key: 'email',
        title: 'Customer email address',
        value: orderDetails.customer?.email,
      },
      {
        key: 'mobileNumber',
        title: 'Customer phone number',
        value: orderDetails.customer?.mobileNumber,
      },
      {
        key: 'cancelledOn',
        title: 'Cancelled on',
        value: dateFormat({
          date: orderDetails.activityDate,
        }),
        hide: orderDetails.orderStatus !== 'CANCELLED',
      },
      {
        key: 'purchaseDate',
        title: 'Purchased on',
        value: dateFormat({
          date: orderDetails.purchaseDate,
        }),
      },
      {
        key: 'storeName',
        title: 'Purchased at',
        value: orderDetails.store?.storeName,
      },
      { key: 'id', title: 'Order ID', value: orderDetails.id },
      {
        key: 'PaymentOption',
        title: 'Payment option',
        value: subscriptionTitleHelper(subscriptionPaymentChoice.numbers),
      },
      {
        key: 'orderReference',
        title: 'Merchant order code',
        value: orderDetails.orderReference,
      },
      {
        key: 'dispatchStatus',
        title: 'Dispatch status',
        value: t(`status.${orderDetails.shipping?.status}`),
      },
      {
        key: 'dispatchDate',
        title: 'Dispatch date',
        value:
          orderDetails?.shipping?.dispatchDate !== null &&
          orderDetails?.shipping?.dispatchDate !== undefined &&
          orderDetails?.shipping?.dispatchDate !== '' &&
          dateFormat({
            date: orderDetails?.shipping?.dispatchDate || '',
            withTime: true,
          }),
      },
    ];
    setLabelsArray(labels);
  }, [orderStatus]);

  useEffect(() => {
    if (!orderId) {
      return;
    }
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useGetOrderDetails(orderId, setOrderDetails, setLoading, dispatch, t);
    getInstallmentDetails(
      orderId,
      orderDetails.currencyCode,
      setProduct,
      setNextPayment,
      setSubscriptionHistory,
      history
    );
  }, [token, orderId, refundModal, triggerApi]);

  const getEligibleDispatchRefund = () => {
    api
      .get({
        endpoint: `orders/${orderId}/dispatch/eligible`,
        headers: {
          Authorization: token,
        },
      })
      .then((resp) => {
        setEligibleDispatch(resp.status === 'success');
      })
      .catch(() => {
        setEligibleDispatch(false);
      });
  };
  const getEligibleRefund = () => {
    api
      .get({
        endpoint: `refunds/subscription/${orderId}/eligibility`,
        headers: {
          Authorization: token,
        },
      })
      .then((resp) => {
        setRefundEligible(resp.data.refundEligible);
        setTotalEligibleRefundSubscriptions(
          resp.data.totalEligibleRefundSubscriptions
        );
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    getEligibleDispatchRefund();
    if (roles.includes(role)) {
      getEligibleRefund();
    }
  }, [triggerApi]);


  const closeChargebackModal = () => {
    setIsChargebackModalOpen(false);
    checkChargebackEligibility();
    getChargebacks({ orderId });
  };

  const dispatchNotification = (
    <div className="dispatch-notification-wrapper">
      Use&nbsp;
      <span className="dispatch-notification">Mark order as dispatched</span>
      &nbsp;to notify the purchase and renewals of the subscription.
    </div>
  );

  return orderId ? (
    <>
      <div className={ns}>
        <Box withHeader className={`${ns}__box`}>
          <div className={`${ns}__cols`}>
            <div>
              <div className={`${ns}__box-grey`}>
                <h2>Subscription Details</h2>
              </div>
              <div className={`${ns}__box-grey`}>
                {labelsArray?.map(({ key, title, value, hide, custom }) =>
                  !hide ? (
                    <p key={key} className={key}>
                      {title}
                      {custom ? (
                        value
                      ) : (
                        <span className={`${ns}__label`}>{value}</span>
                      )}
                    </p>
                  ) : (
                    <></>
                  )
                )}
              </div>
            </div>
            <div>
              {orderDetails?.orderStatus === 'CANCELLED' &&
              roles.slice(1).includes(role) ? (
                <></>
              ) : (
                <div>
                  <>
                    <div className={`${ns}__box-grey`}>
                      <h2>Manage subscription</h2>
                    </div>
                    <div className={`${ns}__box-grey`}>
                      <div>
                        {orderDetails.orderStatus !== 'CANCELLED' && (
                          <>
                            <div className="next-payment-container">
                              <span>Next payment</span>
                              <div className="payment-icon">
                                <PaymentIconSvg />
                              </div>
                            </div>
                            <h1>
                              {priceFormat({
                                currency: orderDetails.currencyCode,
                                amount: orderDetails.totalAmount,
                              })}
                            </h1>
                            <div className="next-payment">
                              <p>
                                <b>
                                  {`Due at ${dateFormat({
                                    date: nextPayment?.chargeDate,
                                  })}`}
                                </b>
                              </p>
                            </div>
                          </>
                        )}

                        <div className="subscription-actions">
                          {refundEligible && (
                            <Button
                              disabled={
                                role === 'CUSTOMER_CARE' ||
                                (role === 'CASHEW' && !refundEligible)
                              }
                              onClick={() => setRefundModal(true)}
                            >
                              {t('orderDetails.button.requestNewRefund')}
                            </Button>
                          )}

                          {eligibleDispatch && (
                            <Button
                              disabled={role === 'CUSTOMER_CARE'}
                              onClick={() => {
                                setShowDispatchModal(true);
                              }}
                            >
                              Mark order as dispatched
                            </Button>
                          )}
                        </div>

                        <div style={{ marginTop: '1rem' }}>
                          {ischargebackEligible && (
                            <Button
                              extraClass={
                                role === 'CUSTOMER_CARE' ? 'disableBtn' : ''
                              }
                              disabled={role === 'CUSTOMER_CARE'}
                              onClick={() => setIsChargebackModalOpen(true)}
                            >
                              Log chargeback
                            </Button>
                          )}
                        </div>
                        {orderDetails.orderStatus !== 'CANCELLED' && (
                          <div className="cancel-renewal">
                            <Button
                              disabled={role === 'CUSTOMER_CARE'}
                              extraClass="cancel-renewal-btn"
                              onClick={() => {
                                if (
                                  orderDetails?.shipping?.status ===
                                  'DISPATCHED'
                                ) {
                                  setShowCancelModal(true);
                                } else {
                                  setShowCancelModalWarning(true);
                                }
                              }}
                            >
                              Cancel Subscription
                            </Button>
                          </div>
                        )}
                        {eligibleDispatch && (
                          <div className="notification__wrapper">
                            <NotificationBadge status="info">
                              {dispatchNotification}
                            </NotificationBadge>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                </div>
              )}
            </div>
            <div className="sub-history">
              <div className={`${ns}__box-grey sub-history-header`}>
                <h2>Payment history</h2>
              </div>
              <Box className="sub-history__list">
                <header>
                  <div>{t('Paid on')}</div>
                  <div>{t('Amount paid')}</div>
                  <div>{t('Status')}</div>
                  <div>{t('Card used')}</div>
                </header>
                <List
                  data={subscriptionHistory.slice(0, 3)}
                  component={SubscriptionHistoryItem}
                  isLoading={false}
                />
              </Box>
              <div
                style={{
                  margin: ' 8px 12px',
                  fontWeight: '700',
                  color: BRAND_COLORS.primary,
                }}
              >
                {subscriptionHistory.length > 3 && (
                  <Link
                    to={{
                      pathname: `/${language}/subscriptions/${orderId}/history`,
                    }}
                  >
                    See all payments
                  </Link>
                )}
              </div>
            </div>
          </div>
        </Box>
      </div>

      {!!chargebacks.length && (
        <div className="list-transactions">
          <ChargebackList
            ns="chargeback-list"
            chargebacks={chargebacks}
            t={t}
          />
        </div>
      )}

      <ConfirmModal
        isVisible={showCancelModalWarning}
        onClose={() => setShowCancelModalWarning(false)}
        confirmTitle=""
        confirmDescription=""
        resultTitle={t('title.warning')}
        resultDescription={t('resultDescription.warning', {
          storeName: `${orderDetails.store?.storeName}`,
        })}
        resultButtons={
          <div>
            <Button
              extraClass="blue"
              onClick={() => {
                setShowCancelModalWarning(false);
              }}
            >
              Ok
            </Button>
          </div>
        }
        confirmButtons={<></>}
        showResult
        showCloseButton={false}
        resultStatus="warning"
      />
      <CancelModal
        orderDetails={orderDetails}
        showConfirmModal={showCancelModal}
        setShowConfirmModal={setShowCancelModal}
        token={token}
        language={language}
        ModalConfirm={ConfirmModal}
        history={history}
        dispatch={dispatch}
        t={t}
        orderId={orderId}
        portalName={portalName}
      />
      <DispatchModal
        orderDetails={orderDetails}
        showConfirmModal={showDispatchModal}
        setShowConfirmModal={setShowDispatchModal}
        language={language}
        ModalConfirm={ConfirmModal}
        history={history}
        dispatch={dispatch}
        t={t}
        orderId={orderId}
        portalName={portalName}
      />
      <RefundModal
        portalName={portalName}
        orderDetails={orderDetails}
        showConfirmModal={refundModal}
        setShowConfirmModal={setRefundModal}
        history={history}
        dispatch={dispatch}
        t={t}
        orderId={orderId}
        subscriptionRefundsNumber={totalEligibleRefundSubscriptions}
        totalPaidSubscritpon={
          subscriptionHistory.filter((item: any) => item.status === 'PAID')
            .length
        }
        setChange={setTriggerApi}
      />
      {isChargebackModalOpen ? (
        <ChargebackModal
          currency={orderDetails?.currencyCode}
          isModalOpen={isChargebackModalOpen}
          closeModal={closeChargebackModal}
          orderId={orderId}
          t={t}
          moment={moment}
        />
      ) : (
        <></>
      )}
    </>
  ) : (
    <div />
  );
};

export default SubscriptionDetailPage;
